import { addDoc, collection, updateDoc, doc, getDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp, deleteDoc, CollectionReference, DocumentData, Firestore } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DeviceTypeModel, DeviceSizeModel, DeviceAccessoryModel, DeviceBrandModel } from '../utils/types';
import { isLocalMode, getLocalList, setLocalList, generateLocalId } from '../utils/localModeHelper';

const ensureFirestore = (): Firestore => {
  if (!db) throw new Error('Firestore is not initialized');
  return db;
};

const getDeviceTypesCollection = (): CollectionReference<DocumentData> => collection(ensureFirestore(), 'deviceTypes');
const getDeviceSizesCollection = (): CollectionReference<DocumentData> => collection(ensureFirestore(), 'deviceSizes');
const getDeviceAccessoriesCollection = (): CollectionReference<DocumentData> => collection(ensureFirestore(), 'deviceAccessories');
const getDeviceBrandsCollection = (): CollectionReference<DocumentData> => collection(ensureFirestore(), 'deviceBrands');

const TYPES_LS = 'deviceTypesList';
const SIZES_LS = 'deviceSizesList';
const ACCESSORIES_LS = 'deviceAccessoriesList';
const BRANDS_LS = 'deviceBrandsList';

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

// ***** Device Types *****
const clearDefaultDeviceTypes = async (): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      const updated = list.map(t => ({ ...t, isDefault: false, updatedAt: new Date() }));
      setLocalList(TYPES_LS, updated);
      return;
    }

    const querySnapshot = await getDocs(query(getDeviceTypesCollection(), where('isDefault', '==', true)));
    const updatePromises = querySnapshot.docs.map(d => updateDoc(d.ref, { isDefault: false, updatedAt: serverTimestamp() }));
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error clearing default device types:', error);
  }
};

export const addDeviceType = async (name: string, isDefault: boolean = false): Promise<DeviceTypeModel> => {
  try {
    if (isDefault) await clearDefaultDeviceTypes();

    const now = new Date();
    const newType: DeviceTypeModel = {
      id: generateLocalId('type'),
      name,
      isActive: true,
      isDefault,
      createdAt: now,
      updatedAt: now
    };

    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      list.push(newType);
      setLocalList(TYPES_LS, list);
      return newType;
    }

    const ref = await addDoc(getDeviceTypesCollection(), {
      name, isActive: true, isDefault,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    const final = { ...newType, id: ref.id };
    const list = getLocalList<DeviceTypeModel>(TYPES_LS);
    list.push(final);
    setLocalList(TYPES_LS, list);
    return final;
  } catch (error) {
    console.error('Error adding device type:', error);
    throw error;
  }
};

export const getAllDeviceTypes = async (activeOnly: boolean = false): Promise<DeviceTypeModel[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      const filtered = activeOnly ? list.filter(t => t.isActive) : list;
      if (filtered.length > 0) {
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      }
    } else {
      const collection = getDeviceTypesCollection();
      const q = activeOnly
        ? query(collection, where('isActive', '==', true), orderBy('name', 'asc'))
        : query(collection, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const types = querySnapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name,
            isActive: data.isActive,
            isDefault: data.isDefault,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt)
          } as DeviceTypeModel;
        });
        setLocalList(TYPES_LS, types);
        return types;
      }
    }

    const stored = getLocalList<DeviceTypeModel>(TYPES_LS);
    if (stored.length > 0) {
      const filtered = activeOnly ? stored.filter(t => t.isActive) : stored;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    const defaultTypes: DeviceTypeModel[] = [
      { id: 'default-phone', name: 'هاتف ذكي', isActive: true, isDefault: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-tablet', name: 'جهاز لوحي (تابلت)', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-laptop', name: 'كمبيوتر محمول', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-other', name: 'أخرى', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(TYPES_LS, defaultTypes);
    return defaultTypes;
  } catch (error) {
    console.error('Error getting device types:', error);
    const stored = getLocalList<DeviceTypeModel>(TYPES_LS);
    if (stored.length > 0) return stored;
    const defaultTypes: DeviceTypeModel[] = [
      { id: 'default-phone', name: 'هاتف ذكي', isActive: true, isDefault: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-tablet', name: 'جهاز لوحي (تابلت)', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-laptop', name: 'كمبيوتر محمول', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-other', name: 'أخرى', isActive: true, isDefault: false, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(TYPES_LS, defaultTypes);
    return defaultTypes;
  }
};

export const updateDeviceType = async (id: string, name: string, isActive: boolean, isDefault?: boolean): Promise<void> => {
  try {
    if (isDefault) await clearDefaultDeviceTypes();

    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      const updated = list.map(t => t.id === id ? { ...t, name, isActive, isDefault: isDefault !== undefined ? isDefault : t.isDefault, updatedAt: new Date() } : t);
      setLocalList(TYPES_LS, updated);
      return;
    }

    const updateData: any = { name, isActive, updatedAt: serverTimestamp() };
    if (isDefault !== undefined) updateData.isDefault = isDefault;
    await updateDoc(doc(getDeviceTypesCollection(), id), updateData);

    const list = getLocalList<DeviceTypeModel>(TYPES_LS);
    const updated = list.map(t => t.id === id ? { ...t, name, isActive, isDefault: isDefault !== undefined ? isDefault : t.isDefault, updatedAt: new Date() } : t);
    setLocalList(TYPES_LS, updated);
  } catch (error) {
    console.error('Error updating device type:', error);
    throw error;
  }
};

export const deleteDeviceType = async (id: string): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS).filter(t => t.id !== id);
      setLocalList(TYPES_LS, list);
      return;
    }

    await deleteDoc(doc(getDeviceTypesCollection(), id));
    const list = getLocalList<DeviceTypeModel>(TYPES_LS).filter(t => t.id !== id);
    setLocalList(TYPES_LS, list);
  } catch (error) {
    console.error('Error deleting device type:', error);
    throw error;
  }
};

export const getDefaultDeviceType = async (): Promise<DeviceTypeModel | null> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      const def = list.find(t => t.isDefault && t.isActive);
      if (def) return def;
      const active = list.filter(t => t.isActive);
      return active.length > 0 ? active[0] : null;
    }

    const q = query(getDeviceTypesCollection(), where('isDefault', '==', true), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const d = querySnapshot.docs[0];
      const data = d.data();
      return {
        id: d.id, name: data.name, isActive: data.isActive, isDefault: data.isDefault,
        createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt)
      } as DeviceTypeModel;
    }
    const allTypes = await getAllDeviceTypes(true);
    return allTypes.length > 0 ? allTypes[0] : null;
  } catch (error) {
    console.error('Error getting default device type:', error);
    return null;
  }
};

export const setDefaultDeviceType = async (id: string): Promise<void> => {
  try {
    await clearDefaultDeviceTypes();
    if (isLocalMode()) {
      const list = getLocalList<DeviceTypeModel>(TYPES_LS);
      const updated = list.map(t => ({ ...t, isDefault: t.id === id, updatedAt: new Date() }));
      setLocalList(TYPES_LS, updated);
      return;
    }
    await updateDoc(doc(getDeviceTypesCollection(), id), { isDefault: true, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('Error setting default device type:', error);
    throw error;
  }
};

// ***** Device Sizes *****
export const addDeviceSize = async (name: string): Promise<DeviceSizeModel> => {
  try {
    const now = new Date();
    const newSize: DeviceSizeModel = { id: generateLocalId('size'), name, isActive: true, createdAt: now, updatedAt: now };

    if (isLocalMode()) {
      const list = getLocalList<DeviceSizeModel>(SIZES_LS);
      list.push(newSize);
      setLocalList(SIZES_LS, list);
      return newSize;
    }

    const ref = await addDoc(getDeviceSizesCollection(), {
      name, isActive: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    const final = { ...newSize, id: ref.id };
    const list = getLocalList<DeviceSizeModel>(SIZES_LS);
    list.push(final);
    setLocalList(SIZES_LS, list);
    return final;
  } catch (error) {
    console.error('Error adding device size:', error);
    throw error;
  }
};

export const getAllDeviceSizes = async (activeOnly: boolean = false): Promise<DeviceSizeModel[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceSizeModel>(SIZES_LS);
      const filtered = activeOnly ? list.filter(s => s.isActive) : list;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const collection = getDeviceSizesCollection();
      const q = activeOnly
        ? query(collection, where('isActive', '==', true), orderBy('name', 'asc'))
        : query(collection, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const sizes = querySnapshot.docs.map(d => {
          const data = d.data();
          return { id: d.id, name: data.name, isActive: data.isActive, createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as DeviceSizeModel;
        });
        setLocalList(SIZES_LS, sizes);
        return sizes;
      }
    }

    const stored = getLocalList<DeviceSizeModel>(SIZES_LS);
    if (stored.length > 0) {
      const filtered = activeOnly ? stored.filter(s => s.isActive) : stored;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    const defaultSizes: DeviceSizeModel[] = [
      { id: 'default-small', name: 'small', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-medium', name: 'medium', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-large', name: 'large', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-xlarge', name: 'extraLarge', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(SIZES_LS, defaultSizes);
    return defaultSizes;
  } catch (error) {
    console.error('Error getting device sizes:', error);
    const stored = getLocalList<DeviceSizeModel>(SIZES_LS);
    if (stored.length > 0) return stored;
    const defaultSizes: DeviceSizeModel[] = [
      { id: 'default-small', name: 'small', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-medium', name: 'medium', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-large', name: 'large', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-xlarge', name: 'extraLarge', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(SIZES_LS, defaultSizes);
    return defaultSizes;
  }
};

export const updateDeviceSize = async (id: string, name: string, isActive: boolean): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceSizeModel>(SIZES_LS);
      const updated = list.map(s => s.id === id ? { ...s, name, isActive, updatedAt: new Date() } : s);
      setLocalList(SIZES_LS, updated);
      return;
    }
    await updateDoc(doc(getDeviceSizesCollection(), id), { name, isActive, updatedAt: serverTimestamp() });
    const list = getLocalList<DeviceSizeModel>(SIZES_LS);
    const updated = list.map(s => s.id === id ? { ...s, name, isActive, updatedAt: new Date() } : s);
    setLocalList(SIZES_LS, updated);
  } catch (error) {
    console.error('Error updating device size:', error);
    throw error;
  }
};

export const deleteDeviceSize = async (id: string): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceSizeModel>(SIZES_LS).filter(s => s.id !== id);
      setLocalList(SIZES_LS, list);
      return;
    }
    await deleteDoc(doc(getDeviceSizesCollection(), id));
    const list = getLocalList<DeviceSizeModel>(SIZES_LS).filter(s => s.id !== id);
    setLocalList(SIZES_LS, list);
  } catch (error) {
    console.error('Error deleting device size:', error);
    throw error;
  }
};

// ***** Device Accessories *****
export const addDeviceAccessory = async (name: string): Promise<DeviceAccessoryModel> => {
  try {
    const now = new Date();
    const newAcc: DeviceAccessoryModel = { id: generateLocalId('acc'), name, isActive: true, createdAt: now, updatedAt: now };

    if (isLocalMode()) {
      const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
      list.push(newAcc);
      setLocalList(ACCESSORIES_LS, list);
      return newAcc;
    }

    const ref = await addDoc(getDeviceAccessoriesCollection(), {
      name, isActive: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    const final = { ...newAcc, id: ref.id };
    const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
    list.push(final);
    setLocalList(ACCESSORIES_LS, list);
    return final;
  } catch (error) {
    console.error('Error adding device accessory:', error);
    throw error;
  }
};

export const getAllDeviceAccessories = async (activeOnly: boolean = false): Promise<DeviceAccessoryModel[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
      const filtered = activeOnly ? list.filter(a => a.isActive) : list;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const collection = getDeviceAccessoriesCollection();
      const q = activeOnly
        ? query(collection, where('isActive', '==', true), orderBy('name', 'asc'))
        : query(collection, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const accessories = querySnapshot.docs.map(d => {
          const data = d.data();
          return { id: d.id, name: data.name, isActive: data.isActive, createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as DeviceAccessoryModel;
        });
        setLocalList(ACCESSORIES_LS, accessories);
        return accessories;
      }
    }

    const stored = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
    if (stored.length > 0) {
      const filtered = activeOnly ? stored.filter(a => a.isActive) : stored;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    const defaultAcc: DeviceAccessoryModel[] = [
      { id: 'default-charger', name: 'الشاحن', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-earphones', name: 'سماعات', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-case', name: 'حافظة', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-memory', name: 'بطاقة ذاكرة', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(ACCESSORIES_LS, defaultAcc);
    return defaultAcc;
  } catch (error) {
    console.error('Error getting device accessories:', error);
    const stored = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
    if (stored.length > 0) return stored;
    const defaultAcc: DeviceAccessoryModel[] = [
      { id: 'default-charger', name: 'الشاحن', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-earphones', name: 'سماعات', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-case', name: 'حافظة', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-memory', name: 'بطاقة ذاكرة', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(ACCESSORIES_LS, defaultAcc);
    return defaultAcc;
  }
};

export const updateDeviceAccessory = async (id: string, name: string, isActive: boolean): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
      const updated = list.map(a => a.id === id ? { ...a, name, isActive, updatedAt: new Date() } : a);
      setLocalList(ACCESSORIES_LS, updated);
      return;
    }
    await updateDoc(doc(getDeviceAccessoriesCollection(), id), { name, isActive, updatedAt: serverTimestamp() });
    const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS);
    const updated = list.map(a => a.id === id ? { ...a, name, isActive, updatedAt: new Date() } : a);
    setLocalList(ACCESSORIES_LS, updated);
  } catch (error) {
    console.error('Error updating device accessory:', error);
    throw error;
  }
};

export const deleteDeviceAccessory = async (id: string): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS).filter(a => a.id !== id);
      setLocalList(ACCESSORIES_LS, list);
      return;
    }
    await deleteDoc(doc(getDeviceAccessoriesCollection(), id));
    const list = getLocalList<DeviceAccessoryModel>(ACCESSORIES_LS).filter(a => a.id !== id);
    setLocalList(ACCESSORIES_LS, list);
  } catch (error) {
    console.error('Error deleting device accessory:', error);
    throw error;
  }
};

// ***** Device Brands *****
export const addDeviceBrand = async (name: string): Promise<DeviceBrandModel> => {
  try {
    const now = new Date();
    const newBrand: DeviceBrandModel = { id: generateLocalId('brand'), name, isActive: true, createdAt: now, updatedAt: now };

    if (isLocalMode()) {
      const list = getLocalList<DeviceBrandModel>(BRANDS_LS);
      list.push(newBrand);
      setLocalList(BRANDS_LS, list);
      return newBrand;
    }

    const ref = await addDoc(getDeviceBrandsCollection(), {
      name, isActive: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    const final = { ...newBrand, id: ref.id };
    const list = getLocalList<DeviceBrandModel>(BRANDS_LS);
    list.push(final);
    setLocalList(BRANDS_LS, list);
    return final;
  } catch (error) {
    console.error('Error adding device brand:', error);
    throw error;
  }
};

export const getAllDeviceBrands = async (activeOnly: boolean = false): Promise<DeviceBrandModel[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceBrandModel>(BRANDS_LS);
      const filtered = activeOnly ? list.filter(b => b.isActive) : list;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const collection = getDeviceBrandsCollection();
      const q = activeOnly
        ? query(collection, where('isActive', '==', true), orderBy('name', 'asc'))
        : query(collection, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const brands = querySnapshot.docs.map(d => {
          const data = d.data();
          return { id: d.id, name: data.name, isActive: data.isActive, createdAt: toDate(data.createdAt), updatedAt: toDate(data.updatedAt) } as DeviceBrandModel;
        });
        setLocalList(BRANDS_LS, brands);
        return brands;
      }
    }

    const stored = getLocalList<DeviceBrandModel>(BRANDS_LS);
    if (stored.length > 0) {
      const filtered = activeOnly ? stored.filter(b => b.isActive) : stored;
      if (filtered.length > 0) return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    const defaultBrands: DeviceBrandModel[] = [
      { id: 'default-samsung', name: 'Samsung', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-apple', name: 'Apple', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-huawei', name: 'Huawei', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-sony', name: 'Sony', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-other', name: 'أخرى', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(BRANDS_LS, defaultBrands);
    return defaultBrands;
  } catch (error) {
    console.error('Error getting device brands:', error);
    const stored = getLocalList<DeviceBrandModel>(BRANDS_LS);
    if (stored.length > 0) return stored;
    const defaultBrands: DeviceBrandModel[] = [
      { id: 'default-samsung', name: 'Samsung', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-apple', name: 'Apple', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-huawei', name: 'Huawei', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-sony', name: 'Sony', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: 'default-other', name: 'أخرى', isActive: true, createdAt: new Date(), updatedAt: new Date() }
    ];
    setLocalList(BRANDS_LS, defaultBrands);
    return defaultBrands;
  }
};

export const updateDeviceBrand = async (id: string, name: string, isActive: boolean): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceBrandModel>(BRANDS_LS);
      const updated = list.map(b => b.id === id ? { ...b, name, isActive, updatedAt: new Date() } : b);
      setLocalList(BRANDS_LS, updated);
      return;
    }
    await updateDoc(doc(getDeviceBrandsCollection(), id), { name, isActive, updatedAt: serverTimestamp() });
    const list = getLocalList<DeviceBrandModel>(BRANDS_LS);
    const updated = list.map(b => b.id === id ? { ...b, name, isActive, updatedAt: new Date() } : b);
    setLocalList(BRANDS_LS, updated);
  } catch (error) {
    console.error('Error updating device brand:', error);
    throw error;
  }
};

export const deleteDeviceBrand = async (id: string): Promise<void> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<DeviceBrandModel>(BRANDS_LS).filter(b => b.id !== id);
      setLocalList(BRANDS_LS, list);
      return;
    }
    await deleteDoc(doc(getDeviceBrandsCollection(), id));
    const list = getLocalList<DeviceBrandModel>(BRANDS_LS).filter(b => b.id !== id);
    setLocalList(BRANDS_LS, list);
  } catch (error) {
    console.error('Error deleting device brand:', error);
    throw error;
  }
};
